<script setup lang="ts">
import { useLandlordAuthStore } from '@afinius/hooks/store/useLandlordAuthStore';
import Button from '@afinius/ui-framework/Button/Button.vue';
import HeightMotion from '@afinius/ui-framework/HeightMotion/HeightMotion.vue';
import Scrollable from '@afinius/ui-framework/Scrollable/Scrollable.vue';
import Stack from '@afinius/ui-framework/Stack/Stack.vue';
import Typography from '@afinius/ui-framework/Typography/Typography.vue';
import { ArrowLeftEndOnRectangleIcon } from '@heroicons/vue/24/solid';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { RouterView, useRoute } from 'vue-router';

const { t } = useI18n();
const route = useRoute();
const authStore = useLandlordAuthStore();

const isLoggingOut = ref(false);

const handleLogout = async () => {
  isLoggingOut.value = true;

  try {
    await authStore.logout();
  } finally {
    isLoggingOut.value = false;
  }
};
</script>

<template>
  <div class="auth-layout-wrapper">
    <div class="auth-layout">
      <Scrollable isAbsolute axis="y">
        <Stack class="auth-layout-body">
          <Stack class="auth-layout-header">
            <slot name="header" />

            <Typography v-if="!route.meta.hideTitle" tag="h1" type="heading" size="xxl">
              {{ route.meta.title }}
            </Typography>
          </Stack>

          <Stack :gap="0" height="100%">
            <div class="auth-layout-content">
              <RouterView />
            </div>

            <div
              v-if="(authStore.isAuthenticated && route.name !== 'auth.terms') || $slots.footer"
              class="auth-layout-body-footer"
            >
              <Stack alignItems="flex-start">
                <Button
                  v-if="authStore.isAuthenticated && route.name !== 'auth.terms'"
                  color="secondary"
                  appearance="outline"
                  :isLoading="isLoggingOut"
                  :onClick="handleLogout"
                >
                  <template #icon>
                    <ArrowLeftEndOnRectangleIcon />
                  </template>

                  {{ t('auth.logout') }}
                </Button>

                <HeightMotion>
                  <Typography v-if="isLoggingOut" size="sm" muted>
                    {{ t('auth.logout-in-progress') }}
                  </Typography>
                </HeightMotion>
              </Stack>

              <slot name="footer" />
            </div>
          </Stack>
        </Stack>
      </Scrollable>
    </div>

    <div v-if="$slots.image" class="auth-layout-image">
      <slot name="image" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use './AuthLayout.scss';
</style>
